import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowUpRight,
  TrendingUp,
  Users,
  Shield,
  Layers,
  Compass,
  Award,
  Sparkles,
  ShieldCheck,
  HeartHandshake,
  Database,
  Workflow,
  Network,
  BarChart3,
} from "lucide-react";
import PublicLayout from "../../components/public/PublicLayout";
import LeadModal from "../../components/public/LeadModal";

export default function MarketingHome() {
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadType, setLeadType] = useState(null);
  const [searchParams] = useSearchParams();

  const openLead = (type = null) => {
    setLeadType(type);
    setLeadOpen(true);
  };

  // Honour ?get-started=mfd|investor deep-links (used by footer column).
  useEffect(() => {
    const v = searchParams.get("get-started");
    if (v === "mfd" || v === "investor") {
      setLeadType(v);
      setLeadOpen(true);
    }
  }, [searchParams]);

  return (
    <PublicLayout title="Beyond Portfolio Reporting">
      <Hero onGetStarted={openLead} />
      <TrustBar />
      <IndustryProblem />
      <Positioning />
      <FeatureGrid />
      <EmotionalStatement />
      <Differentiation />
      <FinalCTA onGetStarted={openLead} />
      {leadOpen && (
        <LeadModal
          onClose={() => setLeadOpen(false)}
          defaultType={leadType}
          source="marketing_home"
        />
      )}
    </PublicLayout>
  );
}

/* ─────────────────── HERO ─────────────────── */
function Hero({ onGetStarted }) {
  return (
    <section
      data-testid="home-hero"
      className="relative overflow-hidden"
    >
      {/* atmospheric backdrop — one soft accent only */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#C8A96B]/[0.06] blur-[160px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-6 md:pt-10 pb-20 md:pb-28">
        <div className="grid lg:grid-cols-12 gap-y-12 gap-x-12 items-start lg:items-center">
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[11px] tracking-[0.2em] uppercase text-white/70 mb-10"
              data-testid="home-hero-eyebrow"
            >
              <Sparkles size={12} className="text-[#C8A96B]" />
              An institutional-grade platform for MFDs
            </div>

            <h1
              data-testid="home-hero-headline"
              className="font-light text-[40px] leading-[1.06] sm:text-[56px] lg:text-[72px] tracking-[-0.025em]"
            >
              Beyond
              <br />
              <span className="font-normal italic text-[#C8A96B]">
                Portfolio Reporting.
              </span>
            </h1>

            <p
              data-testid="home-hero-subhead"
              className="mt-10 text-lg md:text-xl text-white/70 max-w-2xl leading-[1.55] font-light"
            >
              We work alongside MFDs to build practices that scale,
              endure, and create lasting enterprise value — anchored in
              real relationships, not transactions.
            </p>

            <p className="mt-6 text-base text-white/55 max-w-2xl leading-[1.7]">
              Kinntegra brings together reporting infrastructure, MFD
              operations, continuity systems, and succession planning
              under one trusted partnership.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4 flex-wrap">
              <button
                onClick={() => onGetStarted()}
                data-testid="home-hero-cta-primary"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition active:scale-[0.98]"
              >
                Connect With Us
                <ArrowUpRight size={16} />
              </button>
              <Link
                to="/for-arn-holders"
                data-testid="home-hero-cta-arn"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-[#C8A96B]/60 text-[#C8A96B] hover:bg-[#C8A96B]/[0.08] transition"
              >
                For ARN Holders
                <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/about"
                data-testid="home-hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-white/85 hover:bg-white/[0.04] transition"
              >
                Learn More
              </Link>
            </div>
          </div>

          {/* Visual mock */}
          <div className="lg:col-span-5">
            <HeroMock />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroMock() {
  return (
    <div className="relative" data-testid="home-hero-mock">
      <div className="absolute -inset-6 bg-gradient-to-br from-[#C8A96B]/20 to-transparent rounded-3xl blur-2xl" />
      <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur-sm p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-white/40">
              Firm AUM
            </div>
            <div className="text-3xl text-white mt-1 font-light">
              ₹ 711.2 Cr
            </div>
          </div>
          <div className="text-xs px-2.5 py-1 rounded-full bg-[#1a3f3a]/40 text-emerald-300 flex items-center gap-1.5">
            <TrendingUp size={11} /> +14.2%
          </div>
        </div>

        <div className="space-y-2.5">
          {[
            { name: "Active Clients", val: "2,761", w: "w-3/4" },
            { name: "Folios", val: "40,716", w: "w-[88%]" },
            { name: "XIRR (5Y)", val: "16.4%", w: "w-2/3" },
            { name: "Continuity Score", val: "94 / 100", w: "w-[92%]" },
          ].map((r) => (
            <div
              key={r.name}
              className="flex items-center justify-between border-b border-white/5 pb-2 last:border-0"
            >
              <div className="flex-1">
                <div className="text-xs text-white/50">{r.name}</div>
                <div className="mt-1 h-[3px] bg-white/5 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r from-[#C8A96B] to-[#C8A96B]/40 ${r.w}`}
                  />
                </div>
              </div>
              <div className="text-sm text-white/90 ml-4 tabular-nums">
                {r.val}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between text-[11px] text-white/40">
          <span>Last synced: now</span>
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── TRUST BAR ─────────────────── */
function TrustBar() {
  const labels = [
    "MFDs",
    "Independent MFD Firms",
    "Boutique Wealth Businesses",
    "Distribution Practices",
  ];
  return (
    <section className="border-y border-white/5" data-testid="home-trust-bar">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-8 flex flex-wrap items-center justify-between gap-y-3 gap-x-8">
        <div className="text-xs uppercase tracking-[0.22em] text-white/40">
          Built for
        </div>
        {labels.map((l) => (
          <div
            key={l}
            className="text-sm text-white/55 tracking-wide"
          >
            {l}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────── PROBLEM ─────────────────── */
function IndustryProblem() {
  const trad = [
    "Reporting only",
    "Basic dashboards",
    "Transaction visibility",
    "Limited growth infrastructure",
  ];
  const kinn = [
    "Reporting infrastructure",
    "MFD operating system",
    "Continuity planning",
    "Succession readiness",
    "Enterprise building",
    "MFD scalability",
  ];

  return (
    <section
      data-testid="home-industry-problem"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <SectionEyebrow>The industry gap</SectionEyebrow>
        <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-[-0.02em] max-w-3xl leading-[1.12]">
          Most MFD platforms{" "}
          <span className="text-white/40">stop at reporting.</span>
        </h2>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <ComparisonCard
            label="Traditional Platforms"
            items={trad}
            muted
          />
          <ComparisonCard
            label="Kinntegra"
            items={kinn}
            accent
          />
        </div>
      </div>
    </section>
  );
}

function ComparisonCard({ label, items, muted, accent }) {
  return (
    <div
      className={`relative rounded-2xl border p-8 md:p-10 transition ${
        accent
          ? "border-[#C8A96B]/40 bg-gradient-to-br from-[#C8A96B]/[0.06] to-transparent"
          : "border-white/10 bg-white/[0.02]"
      }`}
      data-testid={`compare-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-6">
        <div
          className={`text-xs uppercase tracking-[0.2em] ${
            accent ? "text-[#C8A96B]" : "text-white/40"
          }`}
        >
          {label}
        </div>
        {accent && (
          <span className="text-[10px] uppercase tracking-widest text-[#C8A96B]/80 px-2 py-1 rounded-full bg-[#C8A96B]/10 border border-[#C8A96B]/20">
            Infrastructure
          </span>
        )}
      </div>
      <ul className="space-y-4">
        {items.map((it) => (
          <li
            key={it}
            className={`flex items-baseline gap-3 ${
              muted ? "text-white/55" : "text-white/90"
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full mt-2 ${
                accent ? "bg-[#C8A96B]" : "bg-white/30"
              }`}
            />
            <span className="text-lg font-light">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─────────────────── POSITIONING ─────────────────── */
function Positioning() {
  return (
    <section
      data-testid="home-positioning"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <SectionEyebrow>Positioning</SectionEyebrow>
        <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-[-0.02em] leading-[1.12] max-w-4xl">
          Independent.{" "}
          <span className="italic text-[#C8A96B]">But future-ready.</span>
        </h2>

        <div className="mt-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-x-10 gap-y-6 text-base md:text-lg text-white/65 leading-[1.7] font-light">
            <p>
              Indian MFDs spent decades building trust under their own
              identity. Most do not want to work under someone else's
              umbrella.
            </p>
            <p>
              Kinntegra strengthens MFD independence while providing
              institutional-grade infrastructure designed for long-term
              continuity and growth.
            </p>
          </div>
          <div className="lg:col-span-5">
            <IconVisual
              items={[
                { icon: Users, label: "MFDs" },
                { icon: ShieldCheck, label: "Trust" },
                { icon: Compass, label: "Independence" },
                { icon: HeartHandshake, label: "Relationships" },
                { icon: Award, label: "Practice" },
                { icon: Sparkles, label: "Identity" },
              ]}
              eyebrow="Built alongside MFDs"
              caption="Designed with practising distributors — not borrowed from a different industry."
              testId="positioning-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── FEATURES ─────────────────── */
function FeatureGrid() {
  const feats = [
    {
      icon: Layers,
      title: "Portfolio Reporting",
      copy: "Reconciled across Karvy & CAMS. Folio-level, family-level, and firm-wide.",
    },
    {
      icon: Users,
      title: "Client Dashboards",
      copy: "Premium client-facing experience. Mobile-native, white-label.",
    },
    {
      icon: TrendingUp,
      title: "MFD Operations",
      copy: "SIP / STP / SWP books, transmission workflows, exception logs — all in one.",
    },
    {
      icon: Shield,
      title: "Continuity Infrastructure",
      copy: "Audit trails on every field change. Nothing is ever lost.",
    },
    {
      icon: Compass,
      title: "Succession Planning",
      copy: "Document, structure, and transfer practice value across generations.",
    },
    {
      icon: Award,
      title: "Enterprise Building",
      copy: "Sub-broker associates, scoped books, performance attribution.",
    },
  ];

  return (
    <section
      data-testid="home-features"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <SectionEyebrow>What we built</SectionEyebrow>
        <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-[-0.02em] max-w-3xl leading-[1.12]">
          One platform.{" "}
          <span className="text-white/40">Every layer of your firm.</span>
        </h2>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-white/5 rounded-2xl overflow-hidden border border-white/5">
          {feats.map((f) => (
            <FeatureCard key={f.title} {...f} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ icon: Icon, title, copy }) {
  return (
    <div
      className="group bg-[#07111F] p-8 md:p-10 hover:bg-[#0c1827] transition relative overflow-hidden"
      data-testid={`feature-card-${title.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-[#C8A96B]/40 to-transparent opacity-0 group-hover:opacity-100 transition" />
      <Icon
        size={22}
        className="text-[#C8A96B] mb-7"
        strokeWidth={1.4}
      />
      <h3 className="text-xl font-light text-white mb-3 tracking-tight">
        {title}
      </h3>
      <p className="text-sm text-white/55 leading-relaxed">{copy}</p>
    </div>
  );
}

/* ─────────────────── EMOTIONAL STATEMENT ─────────────────── */
function EmotionalStatement() {
  return (
    <section
      data-testid="home-emotional"
      className="py-20 md:py-28 relative overflow-hidden border-b border-white/5"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #C8A96B 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-[#C8A96B]/[0.08] blur-[140px]" />
      </div>
      <div className="relative max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
        <blockquote className="text-3xl md:text-5xl lg:text-6xl font-light tracking-[-0.02em] leading-[1.18] text-white/95">
          You spent years building trust.{" "}
          <span className="italic text-[#C8A96B]">
            Kinntegra helps ensure that trust survives beyond the founder.
          </span>
        </blockquote>
      </div>
    </section>
  );
}

/* ─────────────────── PRICING TEASER ─────────────────── */
/* ─────────────────── DIFFERENTIATION ─────────────────── */
function Differentiation() {
  const pillars = [
    {
      title: "Infrastructure",
      copy: "Production-grade. Multi-broker reconciled. Built to scale.",
    },
    {
      title: "Independence",
      copy: "Your brand. Your clients. Your firm — strengthened, not absorbed.",
    },
    {
      title: "Continuity",
      copy: "Audit-grade history. No data ever lost. Every change traceable.",
    },
    {
      title: "Enterprise Thinking",
      copy: "Multi-associate roles. Books, AUM, attribution at scale.",
    },
  ];

  return (
    <section
      data-testid="home-differentiation"
      className="py-16 md:py-20 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <SectionEyebrow>Differentiation</SectionEyebrow>
        <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-[-0.02em] max-w-3xl leading-[1.12]">
          More than software.
        </h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-0 lg:gap-12 border-t border-white/5 lg:border-0">
          {pillars.map((p, i) => (
            <div
              key={p.title}
              data-testid={`pillar-${p.title.toLowerCase()}`}
              className={`py-8 lg:py-0 ${
                i !== pillars.length - 1 ? "border-b lg:border-b-0 border-white/5" : ""
              }`}
            >
              <div className="text-[42px] font-extralight text-[#C8A96B]/40 tabular-nums">
                0{i + 1}
              </div>
              <h3 className="mt-4 text-xl font-light text-white tracking-tight">
                {p.title}
              </h3>
              <p className="mt-3 text-sm text-white/55 leading-relaxed max-w-xs">
                {p.copy}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── FINAL CTA ─────────────────── */
function FinalCTA({ onGetStarted }) {
  return (
    <section
      data-testid="home-final-cta"
      className="py-20 md:py-24 relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[360px] w-[720px] rounded-full bg-[#C8A96B]/[0.05] blur-[120px]" />
      </div>
      <div className="relative max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-[-0.02em] leading-[1.12]">
          A conversation, not a sale.
          <br />
          <span className="italic text-[#C8A96B]">Let's begin there.</span>
        </h2>
        <p className="mt-8 text-base md:text-lg text-white/55 font-light leading-[1.7] max-w-xl mx-auto">
          Every Kinntegra relationship starts with a quiet conversation.
          Tell us about your practice or your portfolio, and our team
          will reach out personally.
        </p>
        <div className="mt-12 flex justify-center gap-3 flex-wrap">
          <button
            onClick={() => onGetStarted()}
            data-testid="home-final-cta-primary"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition"
          >
            Schedule a Call
            <ArrowUpRight size={16} />
          </button>
          <Link
            to="/for-arn-holders"
            data-testid="home-final-cta-arn"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-[#C8A96B]/60 text-[#C8A96B] hover:bg-[#C8A96B]/[0.08] transition"
          >
            ARN holder? See pricing
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── shared ─────────────────── */
function SectionEyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/40">
      <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
      {children}
    </div>
  );
}

/**
 * IconVisual — replaces stock photography on marketing surfaces.
 * A 2×3 grid of small gold lucide icons (with tiny labels) on a navy
 * card with subtle dot pattern + corner accents + eyebrow/caption.
 *
 * Pass `items` as an array of { icon, label } objects (6 recommended).
 */
export function IconVisual({ items = [], eyebrow, caption, testId, ratio = "aspect-[4/5]" }) {
  return (
    <div
      data-testid={testId}
      className={`relative rounded-2xl border border-white/10 ${ratio} overflow-hidden bg-gradient-to-br from-[#0c1827] via-[#0a1422] to-[#07111F]`}
    >
      {/* dot pattern */}
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #C8A96B 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      />
      {/* corner ticks */}
      <span className="absolute top-5 left-5 h-3 w-3 border-t border-l border-[#C8A96B]/40" />
      <span className="absolute top-5 right-5 h-3 w-3 border-t border-r border-[#C8A96B]/40" />
      <span className="absolute bottom-5 left-5 h-3 w-3 border-b border-l border-[#C8A96B]/40" />
      <span className="absolute bottom-5 right-5 h-3 w-3 border-b border-r border-[#C8A96B]/40" />
      {/* gold glow */}
      <div className="absolute top-[38%] left-1/2 -translate-x-1/2 -translate-y-1/2 h-72 w-72 rounded-full bg-[#C8A96B]/[0.10] blur-[90px] pointer-events-none" />

      {/* Icon grid (2 cols × 3 rows) */}
      <div className="absolute inset-0 px-7 pt-12 pb-32">
        <div className="grid grid-cols-2 gap-[1px] bg-white/5 rounded-xl overflow-hidden border border-white/10 h-full">
          {items.slice(0, 6).map((it, i) => {
            const Ico = it.icon;
            return (
              <div
                key={i}
                className="bg-[#0a1422]/80 backdrop-blur-sm flex flex-col items-center justify-center gap-2 p-3 hover:bg-[#0c1827] transition group"
              >
                <Ico
                  size={28}
                  strokeWidth={1.2}
                  className="text-[#C8A96B] group-hover:scale-110 transition-transform duration-300"
                />
                <span className="text-[10px] uppercase tracking-[0.14em] text-white/55 text-center leading-tight">
                  {it.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* caption */}
      <div className="absolute bottom-7 left-7 right-7">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#C8A96B]/80 mb-2">
          {eyebrow}
        </div>
        <div className="text-sm text-white/75 font-light leading-snug">
          {caption}
        </div>
      </div>
    </div>
  );
}
